/**
 * Connection registry and event delivery.
 *
 * Push transports (WebSocket, in-process) register a `push` callback; events are delivered immediately.
 * Transports without push (HTTP) get a per-token mailbox that the client drains with `events.poll`
 * (long polling). Services address events to players; the hub fans them out to all of the player's
 * connections and mailboxes.
 *
 * Sequence and replay (design D9): every event addressed to a player gets the player's next `seq` (strictly
 * increasing, the same numbering on every transport) and the server time `ts`. The hub keeps a bounded replay
 * buffer per player (PlayerEventLog: the last `maxEvents` events, each for at most `retentionMs`) that outlives the
 * player's connections, so a client that reconnects asks for the events after the last seq it has
 * (`events.resume`, or `events.poll` over HTTP) and gets exactly the missed ones, or `gap: true` when some of them
 * were dropped from the buffer. The buffer lives in memory: a restart starts new logs whose seqs begin at the
 * creation time in milliseconds, above any seq of an earlier log, so a position from before the restart is
 * reported as a gap too.
 */
import type { EventEnvelope } from '../../shared/protocol/envelope';
import type { EventBatch, EventMap, EventType } from '../../shared/protocol/messages';
import type { PlayerId } from '../../shared/protocol/model';
import { PROTOCOL_VERSION } from '../../shared/protocol/version';
import type { Clock } from '../../shared/clock';

export type TransportKind = 'in-process' | 'http' | 'websocket';

export interface AuthState {
  playerId: PlayerId;
  /**
   * Key of the authenticated credential for mailboxes and rate limits: for a device session a key derived from its
   * session id (services/auth.ts sessionKey), so it stays the same across access token refreshes. Never a token.
   */
  tokenHash: string;
  /** Expiry of the access token the connection was bound with. */
  expiresAt: number;
  /** Device session (services/auth.ts); requests re-check that it was not ended. */
  sessionId?: string;
}

/** What a client said about itself in `system.hello` (kept per connection; logged). */
export interface ClientInfo {
  clientVersion: string;
  /** 'web', 'android', 'ios', 'desktop', 'test', ... (null when the client did not say). */
  platform: string | null;
  capabilities: string[];
}

export interface ConnectionContext {
  readonly connectionId: string;
  readonly transport: TransportKind;
  readonly remoteAddress: string | null;
  /** Push channel; absent for HTTP (events are delivered through `events.poll`). */
  readonly push?: (event: EventEnvelope) => void;
  auth: AuthState | null;
  /** Negotiated protocol version (defaults to the current one). */
  protocol: number;
  /** The client's system.hello (a WebSocket / in-process connection keeps it; every HTTP request is its own context). */
  client?: ClientInfo | null;
}

/** Bounds of a player's replay buffer (BackendConfig.events). */
export interface EventReplayOptions {
  maxEvents: number;
  retentionMs: number;
}

export const DEFAULT_EVENT_REPLAY: EventReplayOptions = { maxEvents: 1000, retentionMs: 30 * 60_000 };

/** An HTTP mailbox without any request of its client for this long is dropped (its events stay in the player's log). */
export const DEFAULT_MAILBOX_IDLE_MS = 120_000;

/** Who reads a player's events: a push connection, or an HTTP client (its token). */
export interface EventViewer {
  connectionId?: string;
  tokenHash?: string;
}

interface LoggedEvent {
  env: EventEnvelope;
  /** The push connection that caused the event: it is not sent to it (nor replayed to it). */
  exceptConnection: string | null;
  /** The HTTP client (token hash) that caused the event: its mailbox skips it. */
  exceptToken: string | null;
}

/** The events of one player with their seqs, bounded by count and age. */
export class PlayerEventLog {
  /** Seq before this log's first event (see the module comment). */
  readonly base: number;
  private seq: number;
  private entries: LoggedEvent[] = [];
  /** Highest seq no longer in the buffer (`base` while nothing was dropped). */
  private droppedThrough: number;
  /** Last time the player had a connection or mailbox (the log is kept `retentionMs` after it). */
  lastActiveAt: number;

  constructor(
    readonly playerId: PlayerId,
    base: number,
    now: number,
    private readonly opts: EventReplayOptions,
  ) {
    this.base = base;
    this.seq = base;
    this.droppedThrough = base;
    this.lastActiveAt = now;
  }

  /** The newest seq (the base while the log has no event yet). */
  get last(): number {
    return this.seq;
  }

  get size(): number {
    return this.entries.length;
  }

  append<E extends EventType>(event: E, payload: EventMap[E], now: number, except?: ConnectionContext): EventEnvelope {
    this.seq += 1;
    const env: EventEnvelope = { v: PROTOCOL_VERSION, event, payload, seq: this.seq, ts: now };
    this.entries.push({
      env,
      exceptConnection: except?.push ? except.connectionId : null,
      exceptToken: except && !except.push ? (except.auth?.tokenHash ?? null) : null,
    });
    const excess = this.entries.length - Math.max(0, this.opts.maxEvents);
    if (excess > 0) this.dropFirst(excess);
    return env;
  }

  private dropFirst(n: number): void {
    const removed = this.entries.splice(0, n);
    const lastSeq = removed[removed.length - 1]?.env.seq;
    if (lastSeq !== undefined) this.droppedThrough = Math.max(this.droppedThrough, lastSeq);
  }

  /** Drops the events older than the retention time. */
  prune(now: number): void {
    const cutoff = now - this.opts.retentionMs;
    let n = 0;
    while (n < this.entries.length && (this.entries[n].env.ts ?? 0) < cutoff) n++;
    if (n > 0) this.dropFirst(n);
  }

  /**
   * The events after `after` that `viewer` gets. `after` undefined or 0 means "no position yet": the events after
   * `start` (never a gap). A position this log cannot serve (events after it were dropped, or it is not a seq of this
   * log: from before a restart or a recreated log) is a gap.
   */
  read(after: number | undefined, viewer: EventViewer, start: number, now: number): EventBatch {
    this.prune(now);
    const noPosition = after === undefined || after === 0;
    const from = noPosition ? Math.max(start, this.droppedThrough) : after;
    if (!noPosition && (from > this.seq || from < this.droppedThrough)) return { events: [], last: this.seq, gap: true };
    const events: EventEnvelope[] = [];
    for (const e of this.entries) {
      if ((e.env.seq ?? 0) <= from) continue;
      if (e.exceptConnection !== null && e.exceptConnection === viewer.connectionId) continue;
      if (e.exceptToken !== null && e.exceptToken === viewer.tokenHash) continue;
      events.push(e.env);
    }
    return { events, last: this.seq, gap: false };
  }
}

interface MailboxWaiter {
  resolve: () => void;
  timer: ReturnType<typeof setTimeout>;
}

/**
 * Long polls (events.poll with waitMs) one mailbox holds at once: a client waits with one, a few tabs of one browser
 * share the token. A further poll answers the oldest waiting one at once (with what there is), so one access token
 * cannot keep thousands of HTTP requests (sockets, timers) open on the server for 25 s each.
 */
export const MAX_WAITING_POLLS = 4;

/** Long-poll state of one HTTP client (token): where it started, when it last polled, its waiting polls. */
export class Mailbox {
  private waiters: MailboxWaiter[] = [];
  private closed = false;
  /** The player's seq when this mailbox was created: a poll without a position starts here. */
  readonly startSeq: number;
  lastPolledAt: number;
  /**
   * The client reads its events here (events.poll, or events.resume over HTTP): only then does the mailbox make its
   * player present (EventHub.isOnline). A client that sends HTTP requests but gets its events elsewhere (a WebSocket
   * next to HTTP calls) or not at all has a mailbox that only collects events.
   */
  polling = false;

  constructor(
    readonly playerId: PlayerId,
    readonly tokenHash: string,
    now: number,
    private readonly log: PlayerEventLog | null,
    private readonly clock: Clock,
  ) {
    this.lastPolledAt = now;
    this.startSeq = log?.last ?? 0;
  }

  /** Events after `after` for this client (see PlayerEventLog.read). */
  since(after?: number): EventBatch {
    if (!this.log) return { events: [], last: 0, gap: false };
    return this.log.read(after, { tokenHash: this.tokenHash }, this.startSeq, this.clock.now());
  }

  /** Waits up to `waitMs` for an event after `after`, then returns what there is. */
  async poll(after: number | undefined, waitMs: number): Promise<EventBatch> {
    const first = this.since(after);
    if (first.events.length > 0 || first.gap || waitMs <= 0 || this.closed) return first;
    await new Promise<void>((resolve) => {
      const waiter: MailboxWaiter = {
        resolve,
        timer: setTimeout(() => {
          this.waiters = this.waiters.filter((x) => x !== waiter);
          resolve();
        }, waitMs),
      };
      while (this.waiters.length >= MAX_WAITING_POLLS) {
        const oldest = this.waiters.shift()!;
        clearTimeout(oldest.timer);
        oldest.resolve();
      }
      this.waiters.push(waiter);
    });
    return this.since(after);
  }

  /** Wakes the waiting polls (a new event of the player). */
  notify(): void {
    const w = this.waiters;
    this.waiters = [];
    for (const x of w) {
      clearTimeout(x.timer);
      x.resolve();
    }
  }

  close(): void {
    this.closed = true;
    this.notify();
  }
}

export class EventHub {
  private readonly connections = new Map<PlayerId, Set<ConnectionContext>>();
  private readonly mailboxes = new Map<string, Mailbox>();
  private readonly playerMailboxes = new Map<PlayerId, Set<Mailbox>>();
  private readonly logs = new Map<PlayerId, PlayerEventLog>();
  private readonly replay: EventReplayOptions;
  /** Highest log base issued in this process (a recreated log continues above it). */
  private lastBase = 0;
  private shuttingDown = false;

  constructor(
    private readonly clock: Clock,
    private readonly mailboxIdleMs = DEFAULT_MAILBOX_IDLE_MS,
    replay: Partial<EventReplayOptions> = {},
  ) {
    this.replay = { ...DEFAULT_EVENT_REPLAY, ...replay };
  }

  /** The player's event log; created when `create` (a connection or mailbox of the player appeared). */
  private logOf(playerId: PlayerId, create: true): PlayerEventLog;
  private logOf(playerId: PlayerId, create: false): PlayerEventLog | null;
  private logOf(playerId: PlayerId, create: boolean): PlayerEventLog | null {
    let log = this.logs.get(playerId) ?? null;
    if (!log && create) {
      const now = this.clock.now();
      const base = Math.max(now, this.lastBase + 1);
      this.lastBase = base;
      log = new PlayerEventLog(playerId, base, now, this.replay);
      this.logs.set(playerId, log);
    }
    return log;
  }

  /** Registers an authenticated push connection. */
  attach(ctx: ConnectionContext): void {
    if (!ctx.auth || !ctx.push) return;
    let set = this.connections.get(ctx.auth.playerId);
    if (!set) this.connections.set(ctx.auth.playerId, (set = new Set()));
    set.add(ctx);
    this.logOf(ctx.auth.playerId, true).lastActiveAt = this.clock.now();
  }

  detach(ctx: ConnectionContext, playerId: PlayerId | null = ctx.auth?.playerId ?? null): void {
    if (!playerId) return;
    const set = this.connections.get(playerId);
    if (!set) return;
    set.delete(ctx);
    if (set.size === 0) this.connections.delete(playerId);
    const log = this.logs.get(playerId);
    if (log) log.lastActiveAt = this.clock.now();
  }

  /** Mailbox of an HTTP client, keyed by its token hash; created on demand. */
  mailbox(auth: AuthState): Mailbox {
    let mb = this.mailboxes.get(auth.tokenHash);
    if (!mb && this.shuttingDown) {
      // Requests still in flight during shutdown get a closed mailbox: long polls return at once.
      mb = new Mailbox(auth.playerId, auth.tokenHash, this.clock.now(), this.logOf(auth.playerId, false), this.clock);
      mb.close();
      return mb;
    }
    if (!mb) {
      const log = this.logOf(auth.playerId, true);
      mb = new Mailbox(auth.playerId, auth.tokenHash, this.clock.now(), log, this.clock);
      this.mailboxes.set(auth.tokenHash, mb);
      let set = this.playerMailboxes.get(auth.playerId);
      if (!set) this.playerMailboxes.set(auth.playerId, (set = new Set()));
      set.add(mb);
    }
    return mb;
  }

  private removeMailbox(tokenHash: string): Mailbox | null {
    const mb = this.mailboxes.get(tokenHash);
    if (!mb) return null;
    mb.close();
    this.mailboxes.delete(tokenHash);
    const set = this.playerMailboxes.get(mb.playerId);
    set?.delete(mb);
    if (set && set.size === 0) this.playerMailboxes.delete(mb.playerId);
    const log = this.logs.get(mb.playerId);
    if (log) log.lastActiveAt = this.clock.now();
    return mb;
  }

  dropMailbox(tokenHash: string): void {
    this.removeMailbox(tokenHash);
  }

  /**
   * Present = at least one push connection (WebSocket, in-process) or a mailbox its client polls (Mailbox.polling);
   * a mailbox that only collects events does not count, so a WebSocket client that also sends HTTP requests goes
   * offline when its WebSocket closes (the disconnect grace starts then).
   */
  isOnline(playerId: PlayerId): boolean {
    if ((this.connections.get(playerId)?.size ?? 0) > 0) return true;
    for (const mb of this.playerMailboxes.get(playerId) ?? []) if (mb.polling) return true;
    return false;
  }

  onlinePlayers(): PlayerId[] {
    const out = new Set<PlayerId>(this.connections.keys());
    for (const [pid, set] of this.playerMailboxes) for (const mb of set) if (mb.polling) out.add(pid);
    return [...out];
  }

  /** The player's newest event seq (0 when the hub keeps no events of the player). */
  lastSeq(playerId: PlayerId): number {
    return this.logs.get(playerId)?.last ?? 0;
  }

  /**
   * The events a reconnected client missed: `after` is the seq of the last event it has (see PlayerEventLog.read;
   * without a position a push connection starts now). HTTP clients read their mailbox (without waiting).
   */
  resume(ctx: ConnectionContext, after: number | undefined): EventBatch {
    if (!ctx.auth) return { events: [], last: 0, gap: false };
    if (!ctx.push) return this.mailbox(ctx.auth).since(after);
    const log = this.logOf(ctx.auth.playerId, true);
    return log.read(after, { connectionId: ctx.connectionId }, log.last, this.clock.now());
  }

  /**
   * Sends an event to players; `except` skips one connection (e.g. the one that caused the event). Every addressed
   * player whose events the hub keeps (connected now or within the retention time) gets it in the replay buffer
   * with the next seq, even while none of its connections is up.
   */
  send<E extends EventType>(playerIds: Iterable<PlayerId>, event: E, payload: EventMap[E], except?: ConnectionContext): void {
    const now = this.clock.now();
    for (const pid of new Set(playerIds)) {
      const log = this.logOf(pid, false) ?? (this.isOnline(pid) ? this.logOf(pid, true) : null);
      if (!log) continue; // not connected lately: a client of this player starts from a fresh state anyway
      const env = log.append(event, payload, now, except);
      for (const ctx of this.connections.get(pid) ?? []) {
        if (ctx === except) continue;
        try {
          ctx.push!(env);
        } catch {
          /* a broken connection must not affect others */
        }
      }
      for (const mb of this.playerMailboxes.get(pid) ?? []) mb.notify();
    }
  }

  /** Sends an event to every connected player (and to the replay buffers of the recently connected ones). */
  broadcast<E extends EventType>(event: E, payload: EventMap[E], except?: ConnectionContext): void {
    this.send([...this.logs.keys(), ...this.onlinePlayers()], event, payload, except);
  }

  /**
   * Drops mailboxes that were not polled recently and the event logs of players offline for longer than the
   * retention time; prunes old events. Returns the players that went offline.
   */
  sweep(now: number): PlayerId[] {
    const dropped: PlayerId[] = [];
    for (const [k, mb] of [...this.mailboxes]) {
      if (now - mb.lastPolledAt > this.mailboxIdleMs) {
        this.removeMailbox(k);
        dropped.push(mb.playerId);
      }
    }
    for (const [pid, log] of [...this.logs]) {
      if (this.isOnline(pid)) {
        log.lastActiveAt = now;
        log.prune(now);
      } else if (now - log.lastActiveAt > this.replay.retentionMs) {
        this.logs.delete(pid);
      } else {
        log.prune(now);
      }
    }
    return [...new Set(dropped)].filter((p) => !this.isOnline(p));
  }

  /** The player's push connections (a copy). */
  connectionsOf(playerId: PlayerId): ConnectionContext[] {
    return [...(this.connections.get(playerId) ?? [])];
  }

  /**
   * Account deletion: everything the hub keeps of the player goes (its mailboxes are closed, its event log with the
   * events a reconnect would have replayed is dropped). The caller closes the player's connections first.
   */
  forgetPlayer(playerId: PlayerId): void {
    for (const mb of [...(this.playerMailboxes.get(playerId) ?? [])]) this.removeMailbox(mb.tokenHash);
    this.playerMailboxes.delete(playerId);
    this.connections.delete(playerId);
    this.logs.delete(playerId);
  }

  /** Cheap counters for /health (no allocation per connection). */
  stats(): { pushConnections: number; mailboxes: number; players: number } {
    let pushConnections = 0;
    for (const set of this.connections.values()) pushConnections += set.size;
    return { pushConnections, mailboxes: this.mailboxes.size, players: this.onlinePlayers().length };
  }

  /** Players whose events are kept for a resume (connected now or within the retention time). */
  eventLogCount(): number {
    return this.logs.size;
  }

  closeAll(): void {
    this.shuttingDown = true;
    for (const mb of this.mailboxes.values()) mb.close();
    this.mailboxes.clear();
    this.playerMailboxes.clear();
    this.connections.clear();
    this.logs.clear();
  }
}
